import { Box } from '@mui/material';
import { PieChart } from '@mui/x-charts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TestrunTooltip from './TestrunTooltip';

const TestrunResult = ({ testrun, width = 420, height, skipAnimation }) => {
    const { t } = useTranslation();

    const seriesArr = useMemo(() => {
        const getPercent = (count, total) => {
            return Math.round((count / total) * 100);
        };
        const newSeriesArr = [];

        const {
            passed_cnt: passed,
            failed_cnt: failed,
            blocked_cnt: blocked,
            broken_cnt: broken,
            failed_na_cnt: failedNa,
            deploy_failed_cnt: deployFailed,
            not_found_cnt: notFound,
            total_cnt: total,
        } = testrun;

        const resultsCount = passed + failed + failedNa + deployFailed + blocked + notFound + broken;
        const notRun = total - resultsCount;

        if (passed) {
            newSeriesArr.push({
                value: passed,
                color: 'var(--semantic-healthy)',
                label: `${t('passed_middle', { lng: 'en' })}\n${passed} (${getPercent(passed, total)}%)`,
            });
        }
        if (failed) {
            newSeriesArr.push({
                value: failed,
                color: 'var(--semantic-charts-error-emphasized)',
                label: `${t('failed_middle', { lng: 'en' })}\n${failed} (${getPercent(failed, total)}%)`,
            });
        }
        if (failedNa) {
            newSeriesArr.push({
                value: failedNa,
                color: 'var(--semantic-charts-error-default)',
                label: `${t('failed_na_middle', { lng: 'en' })}\n${failedNa} (${getPercent(failedNa, total)}%)`,
            });
        }
        if (deployFailed) {
            newSeriesArr.push({
                value: deployFailed,
                color: 'var(--semantic-charts-error-default)',
                label: `${t('deploy_failed_middle', { lng: 'en' })}\n${deployFailed} (${getPercent(deployFailed, total)}%)`,
            });
        }
        if (broken) {
            newSeriesArr.push({
                value: broken,
                color: 'var(--semantic-charts-warning)',
                label: `${t('broken_middle', { lng: 'en' })}\n${broken} (${getPercent(broken, total)}%)`,
            });
        }
        if (blocked) {
            newSeriesArr.push({
                value: blocked,
                color: 'var(--semantic-charts-deep-purple)',
                label: `${t('blocked_middle', { lng: 'en' })}\n${blocked} (${getPercent(blocked, total)}%)`,
            });
        }
        if (notFound) {
            newSeriesArr.push({
                value: notFound,
                color: 'var(--semantic-charts-blue-gray)',
                label: `${t('not_found_middle', { lng: 'en' })}\n${notFound} (${getPercent(notFound, total)}%)`,
            });
        }
        if (notRun > 0) {
            newSeriesArr.push({
                value: notRun,
                color: 'var(--textbox-border-color)',
                label: `${t('not_run_middle', { lng: 'en' })}\n${notRun} (${getPercent(notRun, total)}%)`,
            });
        }
        return newSeriesArr;
    }, [testrun, t]);

    return seriesArr.length ? (
        <Box sx={{ width, height: height || (seriesArr.length <= 2 ? 40 : 80) }}>
            <PieChart
                skipAnimation={skipAnimation}
                series={[
                    {
                        data: seriesArr,
                        cx: 0,
                        cy: 15,
                        innerRadius: 20,
                        outerRadius: 0,
                    },
                ]}
                margin={{ left: 20 }}
                slotProps={{
                    legend: {
                        direction: 'column',
                        position: { vertical: 'top', horizontal: 'left' },
                        itemMarkHeight: 8,
                        itemMarkWidth: 8,
                        itemGap: 16,
                        labelStyle: {
                            fontSize: 14,
                        },
                        padding: { left: 60, top: -6 },
                    },
                }}
                sx={{
                    '& .MuiChartsLegend-mark': {
                        y: -10,
                    },
                }}
                tooltip={{
                    trigger: 'item',
                    itemContent: (props) => <TestrunTooltip total={testrun.total_cnt} mode={'testrun'} {...props} />,
                }}
            />
        </Box>
    ) : (
        '–'
    );
};

export default TestrunResult;
